import * as _ from 'lodash-es';
import {END, Pair, START} from '../abstract-units';
import {Cell} from '../cell';
import {Edge, EdgeSides, getNewEdgeId, otherSide, Side} from '../edge';
import {Layout} from '../layout';
import {LayoutChanges} from '../layout-changes';
import {EdgeActionHistory} from '../layout-history/edge-action-history';
import {EdgeWalker} from '../layout-walkers';
import {SidedEdgesWalker} from '../layout-walkers/sided-edges-walker';
import {CellActionType} from './cell-action-type';

export class EdgeActions {

    constructor(
        public layout: Layout
    ) {}

    public setEdgeDisplayMovingAxisPos(edge: Edge, newDisplayMovingAxisPos: number): LayoutChanges {
        this.layout.history.add(new EdgeActionHistory(edge, [edge.display.movingAxisPos, newDisplayMovingAxisPos]));

        const changes: LayoutChanges = new LayoutChanges();

        this.layout.setLayoutSize(this.layout.displaySize); // Set normal dimensions to be equal to display ones.

        edge.movingAxisPos = newDisplayMovingAxisPos;

        const cells: Cell[] = this.layout.getEdgeCells(edge);

        this.layout.updateEdgesDisplayInfo([edge]);
        this.layout.updateCellsDisplayInfo(cells);

        changes.add(
            'Edge moved',
            {edge, cells},
            {actionType: CellActionType.CELL_CHANGE}
        );

        this.correctEdgeRelatedEdges(edge)
            .appendTo(changes);

        return changes;
    }

    public correctAdjoiningEdges(edge: Edge): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();
        const edgeWalker = new EdgeWalker(edge);
        const sidedAdjoiningEdgesWalker: SidedEdgesWalker = edgeWalker.sidedAdjoiningCrossEdges();

        if (!sidedAdjoiningEdgesWalker.isEmpty()) {
            const adjoiningEdges: Edge[] = sidedAdjoiningEdgesWalker.unitedEdges();

            // Correct adjoining edges: move their ending points.
            sidedAdjoiningEdgesWalker.sidedEdges.forEach((sideEdges: Edge[], side: Side) => {
                sideEdges.forEach(sideEdge => {
                    sideEdge.axisRange[side] = edge.movingAxisPos;
                });
            });

            this.layout.updateEdgesDisplayInfo(adjoiningEdges);

            changes.add('Adjoining edges corrected', {edges: adjoiningEdges});
        }

        return changes;
    }

    public correctEndingCrossEdges(edge: Edge, isDeletion: boolean = false): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();
        const edgeWalker = new EdgeWalker(edge);
        const sidedEndingEdgesWalker: SidedEdgesWalker = edgeWalker.sidedEndingCrossEdges();

        // Correct ending cross edges: split or join their.
        [
            sidedEndingEdgesWalker.firstSidedEdges(),
            sidedEndingEdgesWalker.lastSidedEdges()
        ]
            .forEach(sidedEdges => {
                const [firstSideEdge, secondSideEdge] = sidedEdges;

                if (firstSideEdge && secondSideEdge) {
                    const isSameEdgeOnBothSides = firstSideEdge === secondSideEdge;

                    if (isSameEdgeOnBothSides) {
                        if (!isDeletion) {
                            this.attemptToSplitEdge(firstSideEdge, edge)
                                .appendTo(changes);
                        }
                    } else {
                        this.attemptToJoinEdges(sidedEdges, edge, isDeletion)
                            .appendTo(changes);
                    }
                }
            });

        return changes;
    }

    public correctEdgeRelatedEdges(edge: Edge, isDeletion: boolean = false): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();

        if (!isDeletion) {
            this.correctAdjoiningEdges(edge)
                .appendTo(changes);
        }

        this.correctEndingCrossEdges(edge, isDeletion)
            .appendTo(changes);

        return changes;
    }

    public attemptToSplitEdge(edgeToSplit: Edge, splitterEdge: Edge): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();

        if (!edgeToSplit.isBoundary) {
            const splitterEdgeSide: Side = edgeToSplit.movingAxisPos === splitterEdge.axisRange[START]
                ? Side.FIRST
                : Side.SECOND;
            const continuationEdgeSide = otherSide(splitterEdgeSide);
            const continuationEdge: Edge = this.findContinuationEdge(splitterEdge, edgeToSplit);

            if (continuationEdge) {
                const newEdge: Edge = {
                    id:            getNewEdgeId(),
                    axis:          edgeToSplit.axis,
                    axisRange:     [splitterEdge.movingAxisPos, edgeToSplit.axisRange[END]],
                    movingAxis:    edgeToSplit.movingAxis,
                    movingAxisPos: edgeToSplit.movingAxisPos,
                    sides:         [null, null], // To be populated below.
                    isBoundary:    false,
                    display:       null
                };

                edgeToSplit.axisRange = [
                    edgeToSplit.axisRange[START],
                    splitterEdge.movingAxisPos
                ];

                const edgeToSplitWalker = new EdgeWalker(edgeToSplit);
                const auxiliaryEdges: Pair<Edge> = [splitterEdge, continuationEdge];
                const auxiliaryEdgesSides: Pair<Side> = [splitterEdgeSide, continuationEdgeSide];

                auxiliaryEdgesSides.forEach((side: Side, index) => {
                    const edgeSides: EdgeSides = edgeToSplitWalker
                        .sideCells(side)
                        .splitByEdge(auxiliaryEdges[index]);

                    newEdge.sides[side] = edgeSides[Side.FIRST];
                    edgeToSplit.sides[side] = edgeSides[Side.SECOND];
                });

                const splitEdges = [edgeToSplit, newEdge];

                this.layout.edges.push(newEdge);
                Layout.bindEdgesToCells([newEdge]);
                this.layout.updateEdgesDisplayInfo(splitEdges);

                changes.add('Edge split', {edges: splitEdges});
            }
        }

        return changes;
    }

    public attemptToJoinEdges(
        sidedEdgesToJoin: Pair<Edge>,
        joinerEdge: Edge,
        isDeletion: boolean = false
    ): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();
        const edge: Edge = sidedEdgesToJoin[Side.SECOND];
        const firstSideEdge: Edge = sidedEdgesToJoin[Side.FIRST];
        const canJoin: boolean = isDeletion || !this.findContinuationEdge(joinerEdge, edge);

        console.log(_.cloneDeep({canJoin}));

        if (canJoin) {
            edge.axisRange = [edge.axisRange[START], firstSideEdge.axisRange[END]];
            edge.sides = edge.sides.map(
                (secondSideCells, side: Side) => secondSideCells.concat(firstSideEdge.sides[side])
            ) as EdgeSides;

            _.remove(this.layout.edges, firstSideEdge);
            Layout.bindEdgesToCells([edge]);
            this.layout.updateEdgesDisplayInfo([edge]);

            changes.add('Edges joined', {edge});
        }

        return changes;
    }

    private findContinuationEdge(masterEdge: Edge, axisEdge: Edge): Edge | null {
        const masterEdgeSide: Side = axisEdge.movingAxisPos === masterEdge.axisRange[START]
            ? Side.FIRST
            : Side.SECOND;
        const continuationEdgeSide = otherSide(masterEdgeSide);

        return new EdgeWalker(axisEdge)
            .sideCells(continuationEdgeSide)
            .crossAxisEdges()
            .find(otherEdge => otherEdge.movingAxisPos === masterEdge.movingAxisPos)
            .edge;
    }

}
